import TileWMS from 'ol/source/TileWMS';
import { View, Map } from 'ol';
import { ScaleLine, Zoom, Attribution, FullScreen } from 'ol/control';
import TileLayer from 'ol/layer/Tile';
import { transform, transformExtent, fromLonLat, get as getProjection } from 'ol/proj';
import { defaults } from 'ol/interaction';
import XYZ from 'ol/source/XYZ';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import { MVT, GeoJSON } from 'ol/format';
import { boundingExtent, containsCoordinate } from 'ol/extent';
import Overlay from 'ol/Overlay';
import { Fill, Stroke, Style, Circle } from 'ol/style';
import { asArray } from 'ol/color';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';

import Geolocation from 'ol/Geolocation.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';

import { applyStyle } from 'ol-mapbox-style';
import { isEqual } from 'lodash';


import axios from '@/axios-client.js';
import router from '@/router';
import store from '@/store';
import { retrieveFeatureProperties } from '@/utils';

const parser = new WMTSCapabilities();

let dictLayersToMap = {};
let layersCount = 0;

const geolocationStyle = new Style({
  image: new Circle({
    radius: 6,
    fill: new Fill({
      color: '#3399CC',
    }),
    stroke: new Stroke({
      color: '#fff',
      width: 2,
    }),
  }),
});

const mapService = {
  layers: [],

  mvtLayer: undefined,

  content: {},

  overlay: {},

  map: undefined,

  queryParams: {},

  geolocation: undefined, // for geolocation

  geolocationSource: null, // for geolocation

  positionFeature: null, // for geolocation

  lastPosition: null, // for geolocation

  getMap() {
    return this.map;
  },

  destroyMap() {
    this.map = undefined;
  },

  /**
   * Crée et initialise une carte OpenLayers avec des valeurs sécurisées.
   *
   * Cette fonction garantit que la carte est toujours créée avec un centre et un niveau de zoom valides,
   * même si les paramètres fournis sont absents, vides ou invalides.
   *
   * @param {HTMLElement} el - Élément HTML utilisé comme conteneur de la carte.
   * @param {Object} options - Options d’initialisation de la carte :
   *   - `mapDefaultViewCenter` : centre par défaut `[lat, lng]` (EPSG:4326 ou EPSG:3857)
   *   - `mapDefaultViewZoom` : niveau de zoom par défaut
   *   - `maxZoom` : zoom maximal autorisé
   *   - `interactions` : paramètres d’interaction personnalisés (zoom, drag, etc.)
   *   - `controls` : liste des contrôles OpenLayers à ajouter
   *   - `geolocationControl` : active la géolocalisation si `true`
   *
   * @returns {ol.Map} Instance de la carte OpenLayers initialisée.
   */
  createMap(el, options) {
    const {
      lat,
      lng,
      mapDefaultViewCenter,
      mapDefaultViewZoom,
      maxZoom,
      zoom,
      zoomControl = true,
      fullScreenControl = false,
      geolocationControl = false,
      interactions = { doubleClickZoom: false, mouseWheelZoom: false, dragPan: true },
      controls = [
        new Attribution({ collapsible: false }),
        new ScaleLine({ units: 'metric' }),
      ],
    } = options;

    // Ajoute le contrôle plein écran si demandé.
    if (fullScreenControl) {
      controls.push(new FullScreen({ tipLabel: 'Mode plein écran' }));
    }

    // 🧩 Fonction utilitaire pour vérifier qu'une valeur est bien un nombre valide.
    const isValidNumber = (v) => v !== null && v !== undefined && !isNaN(Number(v));

    // 🧭 Valeurs par défaut issues de la configuration ou utilise les valeurs du backend (fallback déjà géré dans la conf Django)
    const defaultCenter = Array.isArray(mapDefaultViewCenter)
      ? mapDefaultViewCenter
      : [47.0, 1.0];
    const defaultZoom = isValidNumber(mapDefaultViewZoom) ? Number(mapDefaultViewZoom) : 4;

    // ✅ Détermine si des coordonnées explicites sont fournies dans les paramètres (lat/lng en degrés).
    // On ne considère les lat/lng explicites que si les deux sont non nuls
    const hasLatLng = (
      isValidNumber(lat) && isValidNumber(lng) &&
      (Math.abs(Number(lat)) > 0.0001 || Math.abs(Number(lng)) > 0.0001)
    );
    const finalZoom = isValidNumber(zoom) && zoom > 0 ? Number(zoom) : defaultZoom;

    // 🌍 Détection fiable du système de coordonnées via les emprises connues (extent) d'OpenLayers
    const extent3857 = getProjection('EPSG:3857').getExtent() || [-20037508, -20037508, 20037508, 20037508];
    const extent4326 = getProjection('EPSG:4326').getExtent() || [-180, -90, 180, 90];

    const isInProjectionExtent = (coord, projectionExtent) =>
      Array.isArray(coord) && coord.length === 2 && containsCoordinate(projectionExtent, coord);

    // 🧭 Calcul du centre de la carte
    let center;
    if (hasLatLng) {
      // Coordonnées explicites : lat/lng → projeter vers EPSG:3857
      center = transform([Number(lng), Number(lat)], 'EPSG:4326', 'EPSG:3857');
    } else {
      const candidate = defaultCenter.map(Number);
      // 1) Vérifie d’abord si la valeur correspond à une coordonnée en EPSG:4326
      //    On inverse l’ordre car la configuration Geocontrib utilise historiquement le format [lat, lng]
      //    (la norme géographique officielle étant [longitude, latitude] = [x, y])
      const candidateAsLonLat = [candidate[1], candidate[0]];
      if (isInProjectionExtent(candidateAsLonLat, extent4326)) {
        center = transform(candidateAsLonLat, 'EPSG:4326', 'EPSG:3857');
      }
      // 2) Sinon: si ce n’est PAS 4326 mais que ça tient dans 3857, on le garde tel quel (mètres)
      else if (isInProjectionExtent(candidate, extent3857)) {
        center = candidate;
      }
      // 3) Fallback: recentrage par défaut
      else {
        console.warn('⚠️ Coordonnées non reconnues, recentrage par défaut sur la France');
        center = transform([1.0, 47.0], 'EPSG:4326', 'EPSG:3857');
      }
    }

    // 🗺️ Création de la vue OpenLayers
    const view = new View({
      center,
      zoom: finalZoom,
      maxZoom: isValidNumber(maxZoom) ? Number(maxZoom) : 19,
    });

    // ⚙️ Construction de la carte avec options, interactions et contrôles.
    const mapOptions = {
      target: el,
      layers: [],
      controls,
      interactions: defaults(interactions),
      view,
    };

    // 🚀 Initialise et stocke l’instance de la carte.
    this.map = new Map(mapOptions);

    // ➕ Ajout du contrôle de zoom (si activé).
    if (zoomControl) {
      this.map.addControl(new Zoom({ zoomInTipLabel: 'Zoomer', zoomOutTipLabel: 'Dézoomer' }));
    }

    // 📍 Active la géolocalisation si demandée.
    if (geolocationControl) {
      this.initGeolocation();
    }

    // 🧱 Correction automatique de la taille après le premier rendu.
    this.map.once('rendercomplete', () => {
      this.map.updateSize();
    });

    // 🪟 Configuration de l'overlay de popup pour les sélections/infos.
    const container = document.getElementById('popup');
    this.content = document.getElementById('popup-content');
    const closer = document.getElementById('popup-closer');

    this.overlay = new Overlay({
      element: container,
      autoPan: true,
      autoPanAnimation: { duration: 500 },
    });

    // 🔒 Fermeture du popup au clic sur le bouton "x".
    if (closer) {
      closer.onclick = () => {
        this.overlay.setPosition(undefined);
        closer.blur();
        return false;
      };
    }

    // ➕ Ajout de l'overlay et des gestionnaires d'événements.
    this.map.addOverlay(this.overlay);
    this.map.on('click', this.onMapClick.bind(this));

    // 🔄 Gestion de l'événement "change-layers-order" pour maintenir l'ordre d'affichage.
    document.addEventListener('change-layers-order', (event) => {
      // On inverse l’ordre car la première couche dans l’ordre logique doit être ajoutée à la carte en dernier (pour apparaître au-dessus).
      // L’opérateur spread est utilisé pour éviter de modifier le tableau original avec reverse().
      const reversedLayers = [...event.detail.layers].reverse();
      this.updateOrder(reversedLayers);
    });

    return this.map;
  },

  addRouterToPopup({ featureId, featureTypeSlug, index }) {

    const getFeaturePosition = async (searchParams) => {
      const response = await axios.get(`${store.state.configuration.VUE_APP_DJANGO_API_BASE}projects/${router.history.current.params.slug}/feature/${featureId}/position-in-list/?${searchParams.toString()}`);
      return response.data;
    };

    const goToBrowseFeatureDetail = async () => {
      const currentQuery = { ...this.queryParams };
      if (this.queryParams && this.queryParams.filter === 'feature_type_slug') { // when feature_type is the default filter of the project, 
        currentQuery['feature_type_slug'] = featureTypeSlug; // get its slug for the current feature
      }
      const searchParams = new URLSearchParams(currentQuery); // urlSearchParams allow to get rid of undefined values

      if (!index >= 0) { // with mvt feature, there can't be an index
        index = await getFeaturePosition(searchParams);
      }

      router.push({
        name: 'details-signalement-filtre',
        query: {
          ...Object.fromEntries(searchParams.entries()), // transform search params into object and spread it into query
          offset: index
        }
      });
    };

    function goToFeatureDetail() {
      router.push({
        name: 'details-signalement',
        params: {
          slug_type_signal: featureTypeSlug,
          slug_signal: featureId,
        },
      });
    }

    const goToFeatureTypeDetail = () => {
      router.push({
        name: 'details-type-signalement',
        params: {
          feature_type_slug: featureTypeSlug,
        },
      });
    };

    const isFeatureBrowsing = (router.history.current.name === 'project_detail' || router.history.current.name === 'liste-signalements');
    const featureEl = document.getElementById('goToFeatureDetail');
    if (featureEl) featureEl.onclick = isFeatureBrowsing ? goToBrowseFeatureDetail : goToFeatureDetail;
    const featureTypeEl = document.getElementById('goToFeatureTypeDetail');
    if (featureTypeEl) featureTypeEl.onclick = goToFeatureTypeDetail;
  },

  async onMapClick (event) {
    //* retrieve features under pointer
    const features = this.map.getFeaturesAtPixel(event.pixel, {
      layerFilter: (l) => l === this.mvtLayer || this.olLayer
    });
    //* prepare popup content
    if (features && features.length > 0 && this.content) {
      const featureId = features[0].properties_ ? features[0].properties_.feature_id : features[0].id_;
      const isEdited = router.history.current.name === 'editer-signalement' &&
        router.history.current.params.slug_signal === featureId; //* avoid opening popup on feature currently edited
      if (featureId && !isEdited) {
        const popupContent = await this._createContentPopup(features[0]);
        this.content.innerHTML = popupContent.html;
        this.overlay.setPosition(event.coordinate);
        this.addRouterToPopup({
          featureId,
          featureTypeSlug: popupContent.feature_type ? popupContent.feature_type.slug : '',
          index: popupContent.index,
        });
      }
    } else if (this.layers) { // If no feature under the mouse pointer, attempt to find a query layer
      const queryLayer = this.layers.find(x => x.query);
      if (queryLayer) {
        // pour compatibilité avec le proxy django
        const proxyparams = [
          'request',
          'service',
          'srs',
          'version',
          'bbox',
          'height',
          'width',
          'layers',
          'query_layers',
          'info_format', 'x', 'y', 'i', 'j',
        ];
        const url = this.getFeatureInfoUrl(event, queryLayer);
        const urlInfos = url ? url.split('?') : [];
        const urlParams = new URLSearchParams(urlInfos[1]);
        const params = {};
        Array.from(urlParams.keys()).forEach(param => {
          if (proxyparams.indexOf(param.toLowerCase()) >= 0) {
            params[param.toLowerCase()] = urlParams.get(param);
          }
        });
        params.url = urlInfos[0];
        axios.get(
          window.proxy_url,
          { params }
        ).then(response => {
          const data = response.data;
          const err = typeof data === 'object' ? null : data;
          if (data.features || err) this.showGetFeatureInfo(err, event, data, queryLayer);
        }).catch(error => {
          throw error;
        });
      }
    }

  },

  showGetFeatureInfo: function (err, event, data, layer) {
    let content;
    if (err) {
      content = `
          <h4>${layer.options.title}</h4>
          <p>Données de la couche inaccessibles</p>
      `;
      this.content.innerHTML = content;
      this.overlay.setPosition(event.coordinate);
    } else { // Otherwise show the content in a popup
      const contentLines = [];
      let contentTitle;
      if (data.features.length > 0) {
        Object.entries(data.features[0].properties).forEach(entry => {
          const [key, value] = entry;
          if (key !== 'bbox') {
            contentLines.push(`<div>${key}: ${value}</div>`);
          }
        });
        contentTitle = `<h4>${layer.options.title}</h4>`;
        content = contentTitle.concat(contentLines.join(''));

        this.content.innerHTML = content;
        this.overlay.setPosition(event.coordinate);
      }
    }
  },

  getFeatureInfoUrl(event, layer) {
    const olLayer = dictLayersToMap[layer.id];
    const source = olLayer.getSource();
    const viewResolution = this.map.getView().getResolution();
    let url;
    const wmsOptions = { info_format: 'application/json', query_layers: layer.options.layers };
    if (source && source.getFeatureInfoUrl) {
      url = source.getFeatureInfoUrl(event.coordinate, viewResolution, 'EPSG:3857', wmsOptions);
    }
    return url;
  },

  fitBounds(bounds) {
    let ext = boundingExtent([[bounds[0][1], bounds[0][0]], [bounds[1][1], bounds[1][0]]]);
    ext = transformExtent(ext, 'EPSG:4326', 'EPSG:3857');
    this.map.getView().fit(ext, { padding: [25, 25, 25, 25], maxZoom: 16 });

  },

  fitExtent(ext) {
    //ext = transformExtent(ext, 'EPSG:4326', 'EPSG:3857');
    this.map.getView().fit(ext, { padding: [25, 25, 25, 25] });

  },

  /**
   * Add multiple layers to the map. If custom layers are defined, they will be added using `addConfigLayer`.
   * If no custom layers are defined, a default basemap will be added based on the schema type (WMS, WMTS, or XYZ).
   * 
   * @param {Array} layers - Array of layer configurations to be added.
   * @param {string} serviceMap - URL or service for the map base layer.
   * @param {Object} optionsMap - Options for the base layer (e.g., attribution, noWrap).
   * @param {string} schemaType - Type of the base layer (either 'wms', 'wmts', or fallback to XYZ).
   * 
   * @returns {void}
   */
  addLayers: function (layers, serviceMap, optionsMap, schemaType) {
    // Set the current layers to the provided layers array
    this.layers = layers;

    // Check if custom layers are defined (admin-defined basemaps)
    if (layers) {
      // Reset the layer count for managing Z-index
      layersCount = 0;

      // Loop through each layer and add it using the addConfigLayer method
      layers.forEach((layer) => {
        if (!layer) {
          console.error('Layer is missing in the provided layers array.');
        } else {
          this.addConfigLayer(layer);
        }
      });
    } 
    // If no custom layers are defined, fall back to the base map
    else {
      // Ensure that options for the base map are provided
      if (!optionsMap) {
        console.error('Options for the base map are missing.');
        return;
      }

      // Set noWrap to true to prevent map wrapping around the globe
      optionsMap.noWrap = true;

      // Handle the base map based on the schema type (WMS, WMTS, or fallback)
      if (schemaType === 'wms') {
        // Add WMS layer if the schema type is 'wms'
        if (!serviceMap) {
          console.error('Service URL is missing for WMS base layer.');
        } else {
          this.addWMSLayer(serviceMap, optionsMap);
        }
      } else if (schemaType === 'wmts') {
        // Add WMTS layer if the schema type is 'wmts'
        if (!serviceMap) {
          console.error('Service URL is missing for WMTS base layer.');
        } else {
          this.addWMTSLayerFromCapabilities(serviceMap, optionsMap);
        }
      } else {
        // Default to XYZ tile layer if the schema type is not WMS or WMTS
        if (!serviceMap) {
          console.error('Service URL is missing for XYZ base layer.');
        } else {
          const layer = new TileLayer({
            source: new XYZ({
              attributions: optionsMap.attribution, // Attribution for the layer
              url: serviceMap.replace('{s}', '{a-c}') // Handle subdomains in the URL
            })
          });
          this.map.addLayer(layer); // Add the layer to the map
        }
      }
    }
  },

  /**
   * Add a configuration layer (WMS, WMTS, or TMS) to the map based on the layer's schema type.
   * The function handles multiple types of map layers and applies the necessary configurations.
   * 
   * @param {Object} layer - The layer configuration object.
   * @param {Object} layer.options - Options for the layer (e.g., opacity, noWrap).
   * @param {string} layer.schema_type - Type of the layer ('wms', 'wmts', or 'tms').
   * @param {string} layer.service - URL or service for the layer.
   * @param {number} layer.opacity - Opacity of the layer.
   * 
   * @returns {void}
   */
  addConfigLayer: async function (layer) {
    // Check if the layer object is provided
    if (!layer) {
      console.error('Layer object is missing');
      return;
    }

    // Increment the layers count (to manage Z-index)
    layersCount += 1;

    // Extract options from the layer
    const options = layer.options;

    // Check if options are provided for the layer
    if (!options) {
      console.error(`Options are missing for layer: ${layer.id}`);
      return;
    }

    // Set default layer options (noWrap and opacity)
    options.noWrap = true; // Prevent wrapping of the layer around the globe
    options['opacity'] = layer.opacity; // Set opacity based on the layer's configuration

    // Handle WMS layers
    if (layer.schema_type === 'wms') {
      // Add title for queryable WMS layers
      if (layer.queryable) options['title'] = layer.title;
      dictLayersToMap[layer.id] = this.addWMSLayer(layer.service, options); // Add WMS layer
    } 
    // Handle WMTS layers
    else if (layer.schema_type === 'wmts') {
      try {
        const newLayer = await this.addWMTSLayerFromCapabilities(layer.service, options); // Add WMTS layer asynchronously
        dictLayersToMap[layer.id] = newLayer;
      } catch (error) {
        console.error(`Error adding WMTS layer: ${layer.id}`, error);
      }
    } 
    // Handle TMS layers
    else if (layer.schema_type === 'tms') {
      try {
        const newLayer = await this.addTMSLayer(layer.service, options); // Add TMS layer asynchronously
        dictLayersToMap[layer.id] = newLayer;
      } catch (error) {
        console.error(`Error adding TMS layer: ${layer.id}`, error);
      }
    } else {
      console.error(`Unsupported schema type: ${layer.schema_type}`);
    }

    // Set Z-index for the layer if it was successfully added to the map
    if (dictLayersToMap[layer.id]) {
      dictLayersToMap[layer.id].setZIndex(layersCount);
    } else {
      console.error(`Failed to add layer to map: ${layer.id}`);
    }
  },

  addWMSLayer: function (url, options) {
    options.VERSION = options.version || '1.3.0'; // pour compatibilité avec le proxy django
    const source = new TileWMS({
      attributions: options.attribution,
      url: url,
      crossOrigin: 'anonymous',
      params: options
    });

    const layer = new TileLayer({
      source: source,
      opacity: parseFloat(options.opacity),
    });
    this.map.addLayer(layer);
    return layer;
  },

  getWMTSLayerCapabilities: async function (url) {
    // adapted from : https://openlayers.org/en/latest/examples/wmts-layer-from-capabilities.html
    // get capabilities with request to the service
    try {
      const response = await fetch(url);
      const text = await response.text();
      const capabilities = parser.read(text);
      return capabilities;
    } catch (error) {
      console.error(error);
    }
  },
    
  addWMTSLayerFromCapabilities: async function (url, options) {
    // adapted from : https://git.neogeo.fr/onegeo-suite/sites/onegeo-suite-site-maps-vuejs/-/blob/draft/src/services/MapService.ts
    const wmtsCapabilities = await this.getWMTSLayerCapabilities(url);
    const { layer, opacity, attributions, format, ignoreUrlInCapabiltiesResponse } = options;
    let sourceOptions;
    try {
      if (format) {
        sourceOptions = optionsFromCapabilities(wmtsCapabilities, { layer, format });
      } else {
        sourceOptions = optionsFromCapabilities(wmtsCapabilities, { layer });
      }
    }
    catch (e) {
      console.error(e);
      if (e.message == 'projection is null') {
        return 'Projection non reconnue';
      }
      else {
        return 'Problème d\'analyse du getCapabilities';
      }
    }

    if (ignoreUrlInCapabiltiesResponse) {
      var searchMask = 'request(=|%3D)getCapabilities';
      var regEx = new RegExp(searchMask, 'ig');
      var replaceMask = '';
      sourceOptions.urls[0] = url.replace(regEx, replaceMask);
    }
    sourceOptions.attributions = attributions;
    sourceOptions.crossOrigin= 'anonymous';
    if (layer === 'ORTHOIMAGERY.ORTHOPHOTOS') {
      // un peu bourrin mais il semble y avoir qq chose de spécifique avec cette couche ORTHO
      // https://geoservices.ign.fr/documentation/services/utilisation-web/affichage-wmts/openlayers-et-wmts
      sourceOptions.tileGrid = new WMTSTileGrid({
        origin: [-20037508,20037508],
        resolutions: [
          156543.03392804103,
          78271.5169640205,
          39135.75848201024,
          19567.879241005125,
          9783.939620502562,
          4891.969810251281,
          2445.9849051256406,
          1222.9924525628203,
          611.4962262814101,
          305.74811314070485,
          152.87405657035254,
          76.43702828517625,
          38.218514142588134,
          19.109257071294063,
          9.554628535647034,
          4.777314267823517,
          2.3886571339117584,
          1.1943285669558792,
          0.5971642834779396,
          0.29858214173896974,
          0.14929107086948493,
          0.07464553543474241
        ],
        matrixIds: ['0','1','2','3','4','5','6','7','8','9','10','11','12','13','14','15','16','17','18','19'],
      });
    }

    const newLayer = new TileLayer({
      opacity: parseFloat(opacity) || 1, 
      source: new WMTS(sourceOptions),
    });

    this.map.addLayer(newLayer);
    return newLayer;
  },

  /**
   * Add a TMS (Tile Map Service) layer to the map. 
   * If the URL includes `.pbf` (vector tiles), it will apply a style from the provided options.
   * The layer will be added to the map, and the style will be applied asynchronously if needed.
   * 
   * @param {string} url - The URL of the TMS layer service.
   * @param {Object} options - Configuration options for the TMS layer, including opacity and style.
   * @param {string} options.style - URL to the style JSON (required for vector tiles).
   * @param {number} options.opacity - Opacity of the layer (optional).
   * 
   * @returns {VectorTileLayer} - The TMS layer added to the map.
   */
  async addTMSLayer(url, options) {
    // Check if the URL is missing
    if (!url) {
      console.error('TMS layer service URL is missing');
    // Check if the options object is missing
    } else if (!options) {
      console.error('TMS layer options object is missing');
    } else {
      let layerTms;
  
      // Check if the URL refers to PBF (vector tiles)
      if (url.includes('pbf')) {
        // Ensure that a style is provided for vector tiles
        if (!options.style) {
          console.error('TMS layer from PBF requires a style in the options');
        } else {
          // Handle PBF vector tiles
          layerTms = new VectorTileLayer({
            source: new VectorTileSource({
              format: new MVT(), // Format for vector tiles (Mapbox Vector Tiles)
              url: url.replace('{s}', '{a-c}'),  // Handle subdomain pattern in the URL if present
              attributions: options.attribution,
            })
          });
  
          try {
            // Fetch the style JSON from the provided URL
            const response = await fetch(options.style);
            const json = await response.json();

            // Apply the fetched style to the layer (asynchronous)
            await applyStyle(layerTms, json);
          } catch (error) {
            // Handle any errors during the fetch process
            console.error('Error loading the style JSON:', error);
          }
        }
      } else {
        // Handle PNG raster tiles
        layerTms = new TileLayer({
          source: new XYZ({
            url: url.replace('{s}', '{a-c}'), // Use the PNG TMS URL pattern
            attributions: options.attribution,
          })
        });
      }

      // Set the opacity for the layer (default to 1.0 if not specified)
      layerTms.setOpacity(parseFloat(options.opacity || 1.0));

      // Add the TMS layer to the map
      this.map.addLayer(layerTms);

      // Return the TMS layer for further manipulation if needed
      return layerTms;
    }
  },

  // Remove the base layers (not the features)
  removeLayers: function () {
    Object.values(dictLayersToMap).forEach(element => {
      this.map.removeLayer(element);
    });
    dictLayersToMap = {};
  },

  updateOpacity(layerId, opacity) {
    const layer = dictLayersToMap[layerId];
    if (layer) {
      layer.setOpacity(parseFloat(opacity));
    } else {
      console.error(`Layer with id: ${layerId} couldn't be found for opacity update`);
    }
  },

  updateOrder(layers) {
    // First remove existing layers undefined
    layers = layers.filter(function (x) {
      return x !== undefined;
    });
    this.removeLayers();
    // Redraw the layers
    this.addLayers(layers);
  },

  retrieveFeatureStyle: function (featureType, properties) {
    const { colors_style, customfield_set } = featureType;
    let { color, opacity } = featureType;
    if (colors_style && colors_style.custom_field_name && customfield_set) {
      const customField = customfield_set.find((el) => el.name === colors_style.custom_field_name);
      if (customField) {
        const fieldType = customField.field_type;
        let currentValue = properties[colors_style.custom_field_name];
        if (currentValue && typeof currentValue === 'string') currentValue = currentValue.trim(); // remove leading and trailing whitespaces
        switch (fieldType) {
        case 'list':
          if (currentValue) {
            color = colors_style.colors && colors_style.colors[currentValue];
            opacity = colors_style.opacities && colors_style.opacities[currentValue];
          }
          break;
        case 'char': //* if the custom field is supposed to be a string
          //* check if its current value is empty or not, to select a color | https://redmine.neogeo.fr/issues/14048
          color = colors_style.value.colors && colors_style.value.colors[currentValue ? 'Non vide' : 'Vide'];
          opacity = colors_style.value.opacities && colors_style.value.opacities[currentValue ? 'Non vide' : 'Vide'];
          break;
        case 'boolean':
          color = colors_style.value.colors && colors_style.value.colors[currentValue ? 'Coché' : 'Décoché'];
          opacity = colors_style.value.opacities && colors_style.value.opacities[currentValue ? 'Coché' : 'Décoché'];
          break;
        }
      }
    }
    return { color, opacity };
  },

  addVectorTileLayer: function ({ url, project_slug, featureTypes, formFilters = {}, queryParams = {} }) {
    const projectId = project_slug.split('-')[0];
    const format_cfg = {/*featureClass: Feature*/ };
    const mvt = new MVT(format_cfg);

    function customLoader(tile, src) {
      tile.setLoader(function(extent, resolution, projection) {
        const token = () => {
          const re = new RegExp('csrftoken=([^;]+)');
          const value = re.exec(document.cookie);
          return (value != null) ? unescape(value[1]) : null;
        };
        fetch(src, {
          credentials: 'include',
          headers: {
            'X-CSRFToken': token()
          },
        }).then(function(response) {
          response.arrayBuffer().then(function(data) {
            const format = tile.getFormat(); // ol/format/MVT configured as source format
            const features = format.readFeatures(data, {
              extent: extent,
              featureProjection: projection
            });
            tile.setFeatures(features);
          });
        });
      });
    }

    const options = {
      urls: [],
      matrixSet: 'EPSG:3857',
      tileLoadFunction: customLoader,
    };
    options.format = mvt;
    const layerSource = new VectorTileSource(options);
    layerSource.setTileUrlFunction((p0) => {
      return `${url}/?tile=${p0[0]}/${p0[1]}/${p0[2]}&project_id=${projectId}`;
    });
    const styleFunction = (feature) => this.getStyle(feature, featureTypes, formFilters);
    this.mvtLayer = new VectorTileLayer({
      style: styleFunction,
      source: layerSource
    });
    this.featureTypes = featureTypes; // store featureTypes for popups
    this.projectSlug = project_slug; // store projectSlug for popups
    this.queryParams = queryParams; // store queryParams for popups

    this.mvtLayer.setZIndex(30);
    this.map.addLayer(this.mvtLayer);
    window.layerMVT = this.mvtLayer;
  },

  /**
   * Determines the style for a given feature based on its type and applicable filters.
   * 
   * @param {Object} feature - The feature to style.
   * @param {Array} featureTypes - An array of available feature types.
   * @param {Object} formFilters - Filters applied through the form.
   * @returns {ol.style.Style} - The OpenLayers style for the feature.
   */
  getStyle: function (feature, featureTypes, formFilters) {
    const properties = feature.getProperties();
    let featureType;

    // Determine the feature type. Differentiate between GeoJSON and MVT sources.
    if (properties && properties.feature_type) {
      // Handle GeoJSON feature type
      featureType = featureTypes
        .find((ft) => ft.slug === (properties.feature_type.slug || properties.feature_type));
    } else {
      // Handle MVT feature type
      featureType = featureTypes.find((x) => x.slug.split('-')[0] === '' + properties.feature_type_id);
    }

    if (featureType) {
      // Retrieve the style (color, opacity) for the feature.
      const { color, opacity } = this.retrieveFeatureStyle(featureType, properties);
      let colorValue = '#000000'; // Default color

      // Determine the color value based on the feature type.
      if (color && color.value && color.value.length) {
        colorValue = color.value;
      } else if (typeof color === 'string' && color.length) {
        colorValue = color;
      }

      // Convert the color value to RGBA and apply the opacity.
      const rgbaColor = asArray(colorValue);
      rgbaColor[3] = opacity || 0.5; // Default opacity

      // Define the default style for the feature.
      const defaultStyle = new Style({
        image: new Circle({
          fill: new Fill({ color: rgbaColor }),
          stroke: new Stroke({ color: colorValue, width: 2 }),
          radius: 5,
        }),
        stroke: new Stroke({ color: colorValue, width: 2 }),
        fill: new Fill({ color: rgbaColor }),
      });

      // Define a hidden style to apply when filters are active.
      const hiddenStyle = new Style();

      // Apply filters based on feature type, status, and title.
      if (formFilters) {
        if (formFilters.type && formFilters.type.length > 0 && !formFilters.type.includes(featureType.slug)) {
          return hiddenStyle;
        }
        if (formFilters.status && formFilters.status.length > 0 && !formFilters.status.includes(properties.status)) {
          return hiddenStyle;
        }
        if (formFilters.title && !properties.title.toLowerCase().includes(formFilters.title.toLowerCase())) {
          return hiddenStyle;
        }
      }

      // Return the default style if no filters are applied or if the feature passes the filters.
      return defaultStyle;
    } else {
      console.error('No corresponding featureType found.');
      return new Style();
    }
  },

  addFeatures: function ({ features, filter = {}, featureTypes, addToMap = true, project_slug, queryParams = {} }) {
    console.log('addToMap', addToMap);
    const drawSource = new VectorSource();
    let retour;
    let index = 0;
    features.forEach((feature) => {
      try {
        if (feature.properties) {
          feature.properties['index'] = index;
          index += 1;
        }
        retour = new GeoJSON().readFeature(feature, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }, featureTypes);
        drawSource.addFeature(retour);
      } catch (err) {
        console.error(err);
      }
    });
    const styleFunction = (feature) => this.getStyle(feature, featureTypes, filter);
    const olLayer = new VectorLayer({
      source: drawSource,
      style: styleFunction,
    });
    olLayer.setZIndex(29);
    this.map.addLayer(olLayer);
    this.olLayer = olLayer;
    this.drawSource = drawSource;
    this.featureTypes = featureTypes; // store featureTypes for popups
    this.projectSlug = project_slug; // store projectSlug for popups
    this.queryParams = queryParams; // store queryParams for popup routes
    return drawSource;
  },

  removeFeatures: function () {
    this.drawSource.clear();
  },

  addMapEventListener: function (eventName, callback) {
    this.map.on(eventName, callback);
  },

  createCustomFiedsContent(featureType, feature) {
    const { customfield_set } = featureType;
    // generate html for each customField configured to be displayed
    let rows = '';
    for (const { label, name } of customfield_set) {
      const value = feature.getProperties()[name];
      // check if the value is not null nor undefined (to allow false value if boolean)
      if (featureType.displayed_fields.includes(name) && value !== null && value !== undefined) {
        rows += `<div class="customField-row">${label} : ${value}</div>`;
      }
    }
    // wrap all rows into customFields container
    return rows.length > 0 ?
      `<div id="customFields">
        <div class="ui divider"></div>
        <h5>Champs personnalisés</h5>
        ${rows}
      </div>` : '';
  },

  _createContentPopup: async function (feature) {
    const properties = await retrieveFeatureProperties(feature, this.featureTypes, this.projectSlug);
    const { feature_type, index, status, updated_on, created_on, creator, display_last_editor } = properties; // index is used to retrieve feature by query when browsing features
    const { displayed_fields } = feature_type;
    // generate html for each native fields
    const statusHtml = `<div>Statut : ${status}</div>`;
    const featureTypeHtml = `<div>Type de signalement : ${feature_type ? '<a id="goToFeatureTypeDetail" class="pointer">' + feature_type.title + '</a>' : 'Type de signalement inconnu'}</div>`;
    const updatedOnHtml = `<div>Dernière mise à jour : ${updated_on}</div>`;
    const createdOnHtml = `<div>Date de création : ${created_on}</div>`;
    const creatorHtml = creator ? `<div>Auteur : ${creator}</div>` : '';
    const lastEditorHtml = display_last_editor ? `<div>Dernier éditeur : ${display_last_editor}</div>` : '';
    // wrapping up finale html to fill popup, filtering native fields to display and adding filtered customFields
    const html = `<h4>
                    <a id="goToFeatureDetail" class="pointer">${feature.getProperties ? feature.getProperties().title : feature.title}</a>
                  </h4>
                  <div class="fields">
                    ${displayed_fields.includes('status') ? statusHtml : ''}
                    ${displayed_fields.includes('feature_type') ? featureTypeHtml : ''}
                    ${displayed_fields.includes('updated_on') ? updatedOnHtml : ''}
                    ${displayed_fields.includes('created_on') ? createdOnHtml : ''}
                    ${displayed_fields.includes('display_creator') ? creatorHtml : ''}
                    ${displayed_fields.includes('display_last_editor') ? lastEditorHtml : ''}
                    ${this.createCustomFiedsContent(feature_type, feature)}
                  </div>`;
    return { html, feature_type, index };
  },

  zoom(zoomlevel) {
    this.map.getView().setZoom(zoomlevel);
  },

  zoomTo(location, zoomlevel, lon, lat) {
    if (lon && lat) {
      location = [+lon, +lat];
    }
    this.map.getView().setCenter(transform(location, 'EPSG:4326', 'EPSG:3857'));
    this.zoom(zoomlevel);
  },

  animateTo(center, zoom) {
    this.map.getView().animate({ center, zoom });
  },

  addOverlay(loc, zoom) {
    const pos = fromLonLat(loc);
    const marker = new Overlay({
      position: pos,
      positioning: 'center',
      element: document.getElementById('marker'),
      stopEvent: false,
    });
    this.map.addOverlay(marker);
    this.animateTo(pos, zoom);
  },

  initGeolocation() {
    this.geolocation = new Geolocation({
      // enableHighAccuracy must be set to true to have the heading value.
      trackingOptions: {
        enableHighAccuracy: true,
      },
      projection: this.map.getView().getProjection(),
    });
    
    // handle this.geolocation error.
    this.geolocation.on('error', (error) =>  {
      console.error(error.message);
    });
    
    this.positionFeature = new Feature();
    this.positionFeature.setStyle( geolocationStyle );
    
    this.geolocation.on('change:position', () => {
      const currentPosition = this.geolocation.getPosition();
      if (!currentPosition || !isEqual(this.lastPosition, currentPosition)) {
        console.log('current position: ', currentPosition); // keeping this console.log for debug purpose in case needed
      }
      this.lastPosition = currentPosition;
      this.changeTrackerPosition();
    });
    
    this.geolocationSource = new VectorSource({
      features: [this.positionFeature],
    });    
    new VectorLayer({
      map: this.map,
      source: this.geolocationSource,
    });
  },
  
  changeTrackerPosition() {
    if (this.lastPosition) {
      this.positionFeature.setGeometry(new Point(this.lastPosition));
      this.animateTo(this.lastPosition, 16);
    }
  },

  displayGeolocationPoint(isVisible) {
    let features = this.geolocationSource.getFeatures();
    if (!features) return;
    const hiddenStyle = new Style(); // hide the feature
    for (let i = 0; i < features.length; i++) {
      features[i].setStyle(isVisible ? geolocationStyle : hiddenStyle);
    }
  },

  toggleGeolocation(isTracking) {
    if (this.geolocation) {
      this.geolocation.setTracking(isTracking);
      if (this.geolocationSource) {
        this.displayGeolocationPoint(isTracking);
        if (isTracking) {
          this.changeTrackerPosition();
        }
      }
    }
  },

  getMapCenter() {
    const location = this.map.getView().getCenter();
    if (location) {
      return transform(location, 'EPSG:3857', 'EPSG:4326');
    }
    return null;
  }
};

export default mapService;